import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DocumentPersistenceService } from '../../../services/document-persistence.service';
import { DocumentSearchResult } from '../../../../document/models/documentSearchResult';
import { LinkedDocument } from '../../../../shared/models/linked-documents';
import { DocumentLibraryConstants } from '../../../document-library-constants';
import { DocumentAttachmentDetails } from '../../../models/document-attachment-details';
import { PhaseValueConstants } from '../../../../shared/constants/phaseValues-constants';
import { AssignmentGroupSharedDataService } from '../../../../shared/services/assignment-group-shared-data.service';
import { RelatedDocumentsComponent } from '../../../../shared-components/related-documents/related-documents.component';
import { Attachment } from '../../../../document/models/attachment';

@Component({
  selector: 'app-document-related-documents',
  templateUrl: './document-related-documents.component.html',
  styleUrls: ['./document-related-documents.component.scss']
})
export class DocumentRelatedDocumentsComponent implements OnInit, OnDestroy {
  @Output() activateTabIndex = new EventEmitter<number>();
  @Output() enableOtherTabs = new EventEmitter<boolean>();
  @Input() isGlobalDocument ?: boolean;
  @Input() relatedDocuments !: LinkedDocument[];
  
  private destroy$ = new Subject<void>();
  
  showDocumentLibrarySearch: boolean = true;
  errorMessages: string[] = [];
  isReasonForDocumentUpdateRequired = false;
  InactiveAssignmentGroupField : any;
  inactiveGroupsSelected : any;
  isReasonRequired = false;
  isInActiveAssignmentGroupDialog:boolean = false;
  showAddRelatedDocumentBtn: boolean = false;
  isDocumentSaveInProgress: boolean = false;
  documentSaveProgressMessage: string =
    DocumentLibraryConstants.Document_Save_InProgress;
  showRelatedDocumentsNotAlignedWithScopeConfirmationModal: boolean = false;
  documentScopeConfirmationMessage: string =
    DocumentLibraryConstants.RelatedDocuments_Not_Aligned_With_Scope_ConfirmationMessage;
    heading : string = DocumentLibraryConstants.ReasonHeading;
  documentNumbersNotAlignedWithParentDocument: string[] = [];
  showDeletedFilesConfirmationModal = false;
  deletedClientSignatureAttachment ?: Attachment;
  showDeletedClientSignatureAttachmentConfirmationModal = false;
  deletedAttachments: DocumentAttachmentDetails[] = [];
  deleteAttachmentConfirmationMessage: string = DocumentLibraryConstants.Deleted_Attachments_Confirmation_Message;
  deleteClientSignatureAttachmentConfirmationMessage : string = DocumentLibraryConstants.Deleted_ClientSignatureAttachment_Confirmation_Message;
  linkedDocuments: LinkedDocument[]=[]
  documentRelatedDocumentsTab = DocumentLibraryConstants.DocumentRelatedDocumentsTab;

  @ViewChild(RelatedDocumentsComponent)
  public relatedDocumentsComponent!: RelatedDocumentsComponent;
  constructor(
    private documentPersistenceService: DocumentPersistenceService,
    private assignmentGroupSharedDataService:AssignmentGroupSharedDataService) { }

  ngOnInit(): void {
    this.documentPersistenceService.documentSaveInProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (documentSaveStatus) => {
          this.isDocumentSaveInProgress = documentSaveStatus;
        }
      );
    this.documentPersistenceService.currentTabInSliderMenu$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (tabIndex) => {
          if (tabIndex === DocumentLibraryConstants.Document_Menu_RelatedDocuments_Tab_Index ||
            (this.isGlobalDocument && tabIndex === DocumentLibraryConstants.Document_Menu_RelatedDocuments_Tab_Index_GlobalDocument))
          {
            this.updateRelatedDocumentsForSave();
            this.enableOtherTabs.emit(true);
          }
        }
      );
    this.documentPersistenceService.currentTabInSliderMenuByCustomChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (tabIndex) => {
          if (tabIndex === DocumentLibraryConstants.Document_Menu_RelatedDocuments_Tab_Index || 
            (this.isGlobalDocument && tabIndex === DocumentLibraryConstants.Document_Menu_RelatedDocuments_Tab_Index_GlobalDocument)) {
            this.showDocumentLibrarySearch = true;
            this.errorMessages = [];
            this.linkedDocuments = this.documentPersistenceService.linkedDocuments.length > 0? this.documentPersistenceService.linkedDocuments : this.relatedDocuments;
            this.enableOtherTabs.emit(true);
          }
        }
      );
    this.documentPersistenceService.closeAndResetDocumentSlider$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (isClose) => {
          if (isClose) {
            this.resetRelatedDocuments();
          }
        }
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addRelatedDocuments = () => {
    this.validateRelatedDocumentsLinking();
  }

  saveOrUpdateDocument = () => {
    this.updateRelatedDocumentsForSave();
    this.documentPersistenceService.updateIsGlobalDocument(this.isGlobalDocument);
    if (this.documentPersistenceService.isDocumentEditSlider) {
      this.documentSaveProgressMessage = DocumentLibraryConstants.Document_Update_InProgress;
      this.deletedAttachments = this.documentPersistenceService.deletedDocumentAttachments;
      if (this.deletedAttachments.length > 0) {
        this.showDeletedFilesConfirmationModal = true;
      }
      //Below Else is used to show dialog for Reason to Edit and Inactive Groups 
      else {
           //Reset these values everytime
       this.isReasonRequired = false;
       this.inactiveGroupsSelected = undefined;
       this.isReasonForDocumentUpdateRequired = false;
       const documentDetails= this.documentPersistenceService.documentDetails$?.value?.documentVersion;
       this.inactiveGroupsSelected = this.assignmentGroupSharedDataService.findInactiveObjectsInValidatedList(documentDetails,this.documentPersistenceService.validatedUsersList, this.documentPersistenceService.usersList);
       this.addReasonToUpdateDocument();      
      }

    } else {
      this.documentPersistenceService.updateSaveInProgressStatus(true);
      this.documentPersistenceService.saveDocument();
    }
  }

  showConfirmationPopupForDeletedClientSignatureAttachments(){
    this.showDeletedFilesConfirmationModal = false;
    this.deletedClientSignatureAttachment = this.documentPersistenceService.deletedClientSignatureAttachment;
    if(this.deletedClientSignatureAttachment != null){
      this.showDeletedClientSignatureAttachmentConfirmationModal = true;
    }
    else{
      this.addReasonToUpdateDocument()
    } 
  }

  addReasonToUpdateDocument(){
    const phaseValues = [PhaseValueConstants.InReview, PhaseValueConstants.ReadyForApproval, PhaseValueConstants.Approved, PhaseValueConstants.Effective];
    const phaseString = this.documentPersistenceService.documentDetails$.value?.documentVersion.phase?.toString();
    (phaseString && phaseValues.includes(parseInt(phaseString)) && !this.documentPersistenceService.documentDetails$.value?.documentVersion.reasonForDocumentUpdate.length)? this.isReasonForDocumentUpdateRequired = true :  this.isReasonForDocumentUpdateRequired =false; 
    this.documentPersistenceService.updateDeletedAttachments(this.deletedAttachments);
    if(!this.isReasonForDocumentUpdateRequired  && this.inactiveGroupsSelected != undefined && this.inactiveGroupsSelected.length > 0){
      this.InactiveAssignmentGroupField = this.inactiveGroupsSelected;
      this.isInActiveAssignmentGroupDialog = true;
     }
     else if(!this.isReasonForDocumentUpdateRequired)
      this.updateDocument(); 
  }

  closeReasonDailog(value : boolean){    
    this.isReasonForDocumentUpdateRequired = value
  }

  saveDocumentwithReason(value : any){
    this.documentPersistenceService.updatereasonforEditDocument(value);
    this.isReasonForDocumentUpdateRequired = false;
    if(this.inactiveGroupsSelected != undefined && this.inactiveGroupsSelected.length > 0){
      this.isInActiveAssignmentGroupDialog = true;   
      this.InactiveAssignmentGroupField = this.inactiveGroupsSelected;
     }
     else{
        this.updateDocument();
     }
  }

  closeConfirmationDailog(value : boolean){    
    this.isInActiveAssignmentGroupDialog = value
  }
  
  saveDocumentwithInactiveAssignmentGroup(){
    this.isInActiveAssignmentGroupDialog = false;
    this.updateDocument();
  }

  updateDocument = () => {
    this.documentPersistenceService.updateSaveInProgressStatus(true);
    this.documentPersistenceService.updateDocument();
    this.documentPersistenceService.updatereasonforEditDocument('');
  }
  openPreviousTab = () => {
    this.updateRelatedDocumentsForSave();
    if(!this.isGlobalDocument){
      this.activateTabIndex.emit(
        DocumentLibraryConstants.Document_Menu_File_Tab_Index
      );
      this.documentPersistenceService.onCustomTabChange(
        DocumentLibraryConstants.Document_Menu_File_Tab_Index
      );
    }
    else{
      this.activateTabIndex.emit(
        DocumentLibraryConstants.Document_Menu_File_Tab_Index_GlobalDocument
      );
      this.documentPersistenceService.onCustomTabChange(
        DocumentLibraryConstants.Document_Menu_File_Tab_Index_GlobalDocument
      );
    }
  }

  updateRelatedDocumentsForSave = () => {
    this.documentPersistenceService.updateRelatedDocuments(this.relatedDocumentsComponent.linkedDocuments);
  }

  validateRelatedDocumentsLinking = () => {
    this.errorMessages = [];
    if (this.documentPersistenceService.isDocumentEditSlider) {
      this.validateSelfDocumentLinking();
    }
    this.validateIsDocumentLinked();
    if (this.errorMessages.length == 0) {
      this.validateeQMSScope();
    } else {
      document.querySelector('.p-tabview-panels')?.scrollTo(0, 0);
    }
  }

  validateSelfDocumentLinking = () => {
    let documentNumber =
      this.documentPersistenceService.documentDetails$.value?.number || '';
    this.relatedDocumentsComponent.selectedDocuments.forEach((selectedDocument: DocumentSearchResult) => {
      if (selectedDocument.documentNumber === documentNumber) {
        this.errorMessages.push(DocumentLibraryConstants.RelatedDocuments_Self_Linking_ErrorMessage);
      }
    });
  }

  validateeQMSScope = () => {
    let documenteQMSScopeList =
      this.documentPersistenceService.documentDetails$.value?.documentVersion.eQMSScopes || [];
    let selectedDocumentsWithoutSharedScope: string[] = [];
    let iseQMSScopeShared = false;
    this.relatedDocumentsComponent.selectedDocuments.forEach((document) => {
      iseQMSScopeShared = false;
      if (!!document.eQMSScope) {
        document.eQMSScope.forEach(eQMSScope => {
          if (documenteQMSScopeList.filter(eqmsScopeData=>{
            return eqmsScopeData === eQMSScope;
          }).indexOf(eQMSScope) > -1
            && !iseQMSScopeShared) {
            iseQMSScopeShared = true;
          };
        });
        if (!iseQMSScopeShared) {
          selectedDocumentsWithoutSharedScope.push(document.documentNumber);
        }
      } else {
        selectedDocumentsWithoutSharedScope.push(document.documentNumber);
      }
    });
    if (selectedDocumentsWithoutSharedScope.length > 0) {
      this.documentNumbersNotAlignedWithParentDocument = selectedDocumentsWithoutSharedScope;
      this.relatedDocumentsComponent.showRelatedDocumentsNotAlignedWithScopeConfirmationModal = true;
    } else {
      this.relatedDocumentsComponent.updateRelatedDocumentList();
    }
  }
  validateIsDocumentLinked = () => {
    let relatedDocumentNumbers: string[] = this.relatedDocumentsComponent?.linkedDocuments?.map((document) => document.number) || [];
    let alreadyLinkedDocuments = this.relatedDocumentsComponent.selectedDocuments.filter(document => relatedDocumentNumbers.indexOf(document.documentNumber) !== -1);
    if (alreadyLinkedDocuments.length > 0) {
      let documentNumbers = alreadyLinkedDocuments.map((document) => document.documentNumber).join(", ");
      let errorMessageForAlreadyLinkedDocuments: string = alreadyLinkedDocuments.length === 1 ?
        DocumentLibraryConstants.RelatedDocuments_Already_Linked_With_Single_DocumentNumber_ErrorMessage.replace("Document_Number", documentNumbers) :
        DocumentLibraryConstants.RelatedDocuments_Already_Linked_With_Multi_DocumentNumbers_ErrorMessage.replace("Document_Numbers", documentNumbers)
      this.errorMessages.push(errorMessageForAlreadyLinkedDocuments);
    }
  }

  resetRelatedDocuments = () => {
    this.showDocumentLibrarySearch = false;
    this.errorMessages = [];
    this.showAddRelatedDocumentBtn = false;
    this.isDocumentSaveInProgress = false;
    this.showRelatedDocumentsNotAlignedWithScopeConfirmationModal = false;
  }

  closeConfirmationModal = () => {
    this.showDeletedFilesConfirmationModal = false;
  }

  closeConfirmationModalForDeletedClientSignatureAttachments = () =>{
    this.showDeletedClientSignatureAttachmentConfirmationModal = false;
  }
}
